import React from "react";

/**
 * Thrive by Madsen — Direction B: TM Monogram Cartouche.
 * The official brand lockup. Used across the entire site.
 *
 * variant: "compact"  — used in nav, footer aside, page headers
 *          "display"  — used in the hero (large)
 *          "mark"     — cartouche-only (favicon, avatar, bottle cap)
 *
 * tone:    "dark"   — default, ink-on-light surfaces
 *          "light"  — bone-on-dark surfaces (hero, footer, dark pages)
 */
export const Logo = ({ variant = "compact", tone = "dark", className = "" }) => {
  const ink = tone === "light" ? "#F5F5F0" : "#0F0F0F";
  const accent = "#C85A40";

  if (variant === "mark") {
    return <MonogramMark size={48} color={ink} accent={accent} className={className} />;
  }

  if (variant === "display") {
    // Hero-scale lockup
    return (
      <div
        data-testid="brand-wordmark-display"
        className={`flex flex-col items-start gap-6 md:gap-8 ${className}`}
      >
        <MonogramMark
          size={140}
          color={ink}
          accent={accent}
          className="md:!w-[180px] md:!h-[180px]"
        />
        <div className="flex flex-col">
          <span
            className={`font-display tracking-[-0.02em] uppercase font-black leading-[0.85] text-[clamp(4rem,11vw,9.5rem)]`}
            style={{ color: ink }}
          >
            THRIVE
          </span>
          <span
            className="font-display tracking-[0.32em] uppercase font-semibold mt-3 text-[clamp(0.95rem,1.7vw,1.35rem)]"
            style={{ color: ink, opacity: 0.75 }}
          >
            BY MADSEN
          </span>
        </div>
      </div>
    );
  }

  // compact (nav, footer aside, thank-you, cancelled)
  return (
    <a
      href="#top"
      data-testid="brand-wordmark-nav"
      className={`inline-flex items-center gap-3 ${className}`}
      aria-label="Thrive by Madsen — Home"
    >
      <MonogramMark size={42} color={ink} accent={accent} />
      <span className="flex flex-col leading-none">
        <span
          className="font-display tracking-[-0.01em] uppercase font-black text-xl md:text-2xl"
          style={{ color: ink }}
        >
          THRIVE
        </span>
        <span
          className="font-display tracking-[0.28em] uppercase font-semibold text-[0.55rem] md:text-[0.65rem] mt-1"
          style={{ color: ink, opacity: 0.7 }}
        >
          BY MADSEN
        </span>
      </span>
    </a>
  );
};

/**
 * The TM Monogram cartouche — works as favicon, avatar, app icon.
 * Used standalone, or inside the Logo lockups above.
 */
export const MonogramMark = ({
  size = 48,
  color = "#0F0F0F",
  accent = "#C85A40",
  className = "",
}) => (
  <svg
    viewBox="0 0 200 200"
    width={size}
    height={size}
    role="img"
    aria-label="Thrive by Madsen monogram"
    className={className}
    data-testid="brand-monogram"
  >
    {/* Cartouche frame */}
    <rect
      x="12"
      y="12"
      width="176"
      height="176"
      fill="none"
      stroke={color}
      strokeWidth="6"
    />
    {/* T glyph — sharp slab serif */}
    <path
      d="M 40 50 L 160 50 L 160 70 L 109 70 L 109 158 L 91 158 L 91 70 L 40 70 Z"
      fill={color}
    />
    {/* Terracotta slab — the Madsen signature line */}
    <rect x="40" y="166" width="120" height="9" fill={accent} />
    {/* Micro 'm' tag */}
    <text
      x="178"
      y="186"
      textAnchor="end"
      fontFamily="'Outfit', sans-serif"
      fontWeight="900"
      fontSize="18"
      letterSpacing="1"
      fill={accent}
    >
      m
    </text>
  </svg>
);

/* Backwards-compat alias so any older import keeps working */
export const Monogram = MonogramMark;
